import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';

import debounce from 'lodash/debounce';
import { useRecoilValue } from 'recoil';

import { ia, iaRa, snakeToTitleCase } from 'lib/helpers/utility';
import { useInfiniteFetchData } from 'lib/hooks/useInfiniteFetchData';

import { currentPractice } from 'components/practice/practiceState';
import Icon from 'components/shared/Icon/Icon';
import state from 'components/state';

import ActivitiesLogSkeleton from './ActivitiesLogSkeleton';
import ActivityLog from './components/ActivityLog';
import Comment from './components/Comment';
import CommentInput from './components/CommentInput';
import NoActivities from './components/NoActivities';
import { exportActivitiesToPdf } from './lib/exportActivitiesToPdf';
import { useComment } from './useComment';

const ActivitiesLog = forwardRef(({ id, fetchData, category }, ref) => {
  const scrollRef = useRef(null);
  const observer = useRef(null);
  const userState = useRecoilValue(state.userState);
  const permission = useRecoilValue(state.permissions);
  const practice = useRecoilValue(currentPractice);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteFetchData({
    queryKey: 'activities',
    queryFn: fetchData,
    params: { id },
    dependencies: [category, id],
    options: {
      staleTime: Infinity
    }
  });

  const { isCreatingComment, onCommentPostHandler, onCommentDeleteHandler, onCommentSaveHandler } =
    useComment(category, id);

  const sortedData = useMemo(() => {
    return iaRa(data).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [data]);

  const handleExportPdf = useCallback(() => {
    const title = `${snakeToTitleCase(category) || ''} Activity Log`;
    exportActivitiesToPdf(sortedData, title, practice);
  }, [sortedData, category, practice]);

  useImperativeHandle(ref, () => ({ handleExportPdf }), [handleExportPdf]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [isLoading, isCreatingComment]);

  const firstItemRef = useCallback(
    (node) => {
      if (isLoading || isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        debounce((entries) => {
          if (entries[0].isIntersecting && hasNextPage) {
            const scrollContainer = scrollRef.current;
            if (scrollContainer) {
              const firstItemOffset = node?.offsetTop ?? 0;
              const previousScrollHeight = scrollContainer.scrollHeight;

              fetchNextPage().then(() => {
                requestAnimationFrame(() => {
                  const newScrollHeight = scrollContainer.scrollHeight;
                  const heightDifference = newScrollHeight - previousScrollHeight;
                  scrollContainer.scrollTop = firstItemOffset + heightDifference;
                });
              });
            }
          }
        }, 200)
      );

      if (node) observer.current.observe(node);
    },
    [isLoading, isFetchingNextPage, hasNextPage, fetchNextPage]
  );

  if (isLoading) return <ActivitiesLogSkeleton />;

  return (
    <div
      ref={scrollRef}
      className="flex flex-1 flex-col justify-between gap-5 overflow-y-auto px-3 pb-3 pt-0">
      {isFetchingNextPage && (
        <div className="align-center flex justify-center pb-2">
          <Icon icon="loader-linear" className="animate-spin" color="primary" size={28} />
        </div>
      )}
      <div className="flex flex-col gap-5">
        {iaRa(sortedData).map((activity, index) => {
          const isFirstItem = index === 0;
          return (
            <div key={index} ref={isFirstItem ? firstItemRef : null}>
              {activity.type === 'comment' ? (
                <Comment
                  comment={activity}
                  onDelete={onCommentDeleteHandler}
                  onSave={onCommentSaveHandler}
                  creatorId={permission?.admin_id || userState?.id}
                />
              ) : (
                <ActivityLog activity={activity} />
              )}
            </div>
          );
        })}
      </div>
      {!ia(data) && <NoActivities />}
      <CommentInput loading={isCreatingComment} onPost={onCommentPostHandler} />
    </div>
  );
});

ActivitiesLog.displayName = 'ActivitiesLog';

export default ActivitiesLog;
